import React, { useState, useEffect } from "react";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import ShippingProductDisplay from "./ShippingProductsDisplay";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { place_order } from "../store/reducers/orderReducer";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import toast from "react-hot-toast";
import { Globe, MapPin, MessageCircle } from "lucide-react";
import { Package, ScrollText, AlertTriangle } from "lucide-react";
import { formatCurrency } from "../utils/currencyUtils";
import { useCurrency } from "../hooks/useCurrency"
import axios from "../api/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const Shipping = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currencyCode, exchangeLoaded } = useCurrency()
  const { userInfo } = useSelector((state) => state.auth);
  const {
    state: { products, price, items },
  } = useLocation();

  const [res, setRes] = useState(false);
  const [totalShippingCost, setTotalShippingCost] = useState(0);
  const [invalidProducts, setInvalidProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [useDefaultAddress, setUseDefaultAddress] = useState(false);
  const [defaultShippingInfo, setDefaultShippingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taxRates, setTaxRates] = useState({});
  const [taxAmount, setTaxAmount] = useState(0);

  // Installment state
  const [installmentSettings, setInstallmentSettings] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('full');
  const [numInstallments, setNumInstallments] = useState(null);

  const [state, setState] = useState({
    name: "",
    address: "",
    phone: "",
    post: "",
    province: "",
    city: "",
    area: "",
    country: "",
  });

  const availableCountries = [
    ...new Set(
      products.flatMap((p) =>
        p.products.flatMap((prod) =>
          prod.productInfo.shippingPrices
            ? Object.keys(prod.productInfo.shippingPrices)
            : []
        )
      )
    ),
  ];

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const { data } = await axios.get("/account/get-details", {
          withCredentials: true,
        });

        if (data.shippingDetails && !defaultShippingInfo) {
          setDefaultShippingInfo({
            name: data.shippingDetails.name || "",
            address: data.shippingDetails.address || "",
            phone: data.shippingDetails.phone || "",
            post: data.shippingDetails.postal || "",
            province: data.shippingDetails.province || "",
            city: data.shippingDetails.city || "",
            area: data.shippingDetails.area || "",
            country: data.shippingDetails.country || "",
          });
        }
      } catch (error) {
        // Handle error silently
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [defaultShippingInfo]); // Add defaultShippingInfo as a dependency

  useEffect(() => {
    axios.get('/tax-rates').then(({ data }) => {
      setTaxRates(data.rates || {})
    }).catch(() => {})
  }, []);

  useEffect(() => {
    axios.get('/installment/settings').then(({ data }) => {
      setInstallmentSettings(data)
    }).catch(() => {})
  }, []);

  useEffect(() => {
    if (state.country) {
      calculateShippingCost(state.country);
      const rate = taxRates[state.country] || 0;
      setTaxAmount(rate > 0 ? parseFloat(((price * rate) / 100).toFixed(2)) : 0);
    }
  }, [state.country, taxRates]); // Only run when state.country or taxRates changes

  useEffect(() => {
    if (invalidProducts.length > 0 && res) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [invalidProducts.length, res]);

  const calculateShippingCost = (country) => {
    let totalShipping = 0;
    let invalid = [];

    products.forEach((shop) => {
      shop.products.forEach((prod) => {
        const shippingPrices = prod.productInfo.shippingPrices || {};
        if (shippingPrices[country]) {
          totalShipping += Number(shippingPrices[country]);
        } else {
          invalid.push(prod.productInfo._id);
        }
      });
    });

    setTotalShippingCost(totalShipping);
    setInvalidProducts(invalid);
  };

  const handleDefaultAddressChange = (checked) => {
    setUseDefaultAddress(checked);

    if (checked && defaultShippingInfo) {
      
      if (!availableCountries.includes(defaultShippingInfo.country)) {
        toast.error("Products cannot be shipped to your default location.");
        return; 
      }

      setState(defaultShippingInfo);
    } else {

      setState({
        name: "",
        address: "",
        phone: "",
        post: "",
        province: "",
        city: "",
        area: "",
        country: "",
      });
    }
  };

  useEffect(() => {
    if (useDefaultAddress && defaultShippingInfo) {
      // Sync state with defaultShippingInfo when useDefaultAddress is true
      setState(defaultShippingInfo);
    }
  }, [useDefaultAddress, defaultShippingInfo]);

  useEffect(() => {
    console.log("useDefaultAddress changed:", useDefaultAddress);
  }, [useDefaultAddress]);

  useEffect(() => {
    console.log("State updated:", state);
  }, [state]);

  useEffect(() => {
    console.log("defaultShippingInfo updated:", defaultShippingInfo);
  }, [defaultShippingInfo]);

  const inputHandle = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const handleCountryChange = (value) => {
    setState({
      ...state,
      country: value,
    });
  };

  const save = (e) => {
    e.preventDefault();
    const { name, address, phone, post, province, city, area, country } = state;

    const emptyFields = [];
    if (!name) emptyFields.push("Name");
    if (!address) emptyFields.push("Address");
    if (!phone) emptyFields.push("Phone");
    if (!post) emptyFields.push("Postal code");
    if (!province) emptyFields.push("Province");
    if (!city) emptyFields.push("City");
    if (!area) emptyFields.push("Area");
    if (!country) emptyFields.push("Country");

    if (emptyFields.length > 0) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (invalidProducts.length === 0) {
      setRes(true);
    } else {
      setShowModal(true);
    }
  };
  const omahServiceFee = Math.round((price * 0.10) * 100) / 100;
  const totalOrderPrice = price + totalShippingCost + omahServiceFee;

  const installmentEligible =
    installmentSettings?.enabled &&
    totalOrderPrice >= (installmentSettings?.minAmount || 0);

  const interestRate = installmentSettings?.interestRate || 0;
  const totalWithInterest = totalOrderPrice * (1 + interestRate / 100);
  const firstInstallmentAmount =
    numInstallments ? Math.ceil((totalWithInterest / numInstallments) * 100) / 100 : 0;

  const placeOrder = () => {
    dispatch(
      place_order({
        price: totalOrderPrice,
        products,
        shippingInfo: state,
        userId: userInfo.id,
        navigate,
        items,
        taxAmount: 0,
        paymentMethod: installmentEligible && paymentMethod === 'installment' ? 'installment' : 'full',
        numInstallments: installmentEligible && paymentMethod === 'installment' ? numInstallments : null,
      })
    );
  };

  return (
    <div>
      <Headers />
      <section
        className="h-[220px] bg-cover bg-no-repeat relative bg-left"
        style={{
          backgroundImage: `url(${process.env.REACT_APP_URL}/images/banner/cartnew.jpg)`,
        }}
      >
        <div className="absolute left-0 top-0 w-full h-full bg-[#0000008a]">
          <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
            <div className="flex flex-col justify-center gap-1 items-center h-full w-full text-white">
              <h2 className="text-3xl font-bold">Omah Global</h2>
              <div className="flex justify-center items-center gap-2 text-2xl w-full">
                <Link to="/">Home</Link>
                <span className="pt-2">
                  <MdOutlineKeyboardArrowRight />
                </span>
                <span>Shipping</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="">
        <div className="w-[85%] lg:w-[90%] md:w-[90%] sm:w-[90%] mx-auto py-16">
          <div className="w-full flex flex-wrap">
            <div className="w-[67%] md-lg:w-full">
              <div className="flex flex-col gap-3">
                <div className="bg-white border border-gray-100 shadow-sm p-6 rounded-md">
                  {!res && (
                    <>
                      <h2 className="text-xl font-bold flex items-center pb-3 text-gray-900">
                        Shipping Information
                      </h2>
                      {!loading && defaultShippingInfo && (
                        <div className="flex items-center space-x-2 mb-4">
                          <Checkbox
                            id="useDefault"
                            checked={useDefaultAddress}
                            onCheckedChange={handleDefaultAddressChange}
                            disabled={!defaultShippingInfo} // Disable if defaultShippingInfo is not available
                          />
                          <label
                            htmlFor="useDefault"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-gray-700"
                          >
                            Use my default shipping address
                          </label>
                        </div>
                      )}
                      <div className="flex gap-4 p-4 bg-blue-500/10 text-blue-500 rounded-lg border border-blue-500/20 mb-4">
                        <Globe className="h-5 w-5 flex-shrink-0 mt-1" />
                        <div className="space-y-3">
                          <p className="text-sm">
                            Important information about shipping availability:
                          </p>
                          <ul className="text-sm space-y-2">
                            <li className="flex items-start gap-2">
                              <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              The countries you see in the dropdown are the only
                              ones where merchants have established shipping
                              routes for their products in your cart.
                            </li>
                            <li className="flex items-start gap-2">
                              <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              If you don't see your desired destination country,
                              this means the merchants currently don't ship to
                              that location.
                            </li>
                            <li className="flex items-start gap-2">
                              <MessageCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              Please contact the merchant directly to inquire
                              about additional shipping options or potential
                              solutions for your desired destination.
                            </li>
                          </ul>
                        </div>
                      </div>
                      <form
                        onSubmit={save}
                        className="flex flex-col gap-2 mt-5"
                      >
                        <div className="flex md:flex-col md:gap-2 w-full gap-5">
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <Label htmlFor="name" className="text-gray-700">
                              Name
                            </Label>
                            <Input
                              onChange={inputHandle}
                              value={state.name}
                              type="text"
                              className="w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground"
                              name="name"
                              placeholder="Name"
                              id="name"
                            />
                          </div>
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <Label htmlFor="country" className="text-gray-700">
                              Country
                            </Label>
                            <Select
                              onValueChange={handleCountryChange}
                              value={state.country}
                            >
                              <SelectTrigger className="w-full mt-2 bg-transparent h-10 focus-visible:border-foreground border rounded-lg">
                                <SelectValue placeholder="Select country" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200 text-gray-700">
                                {availableCountries.map((country) => (
                                  <SelectItem key={country} value={country}>
                                    {country}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="flex md:flex-col md:gap-2 w-full gap-5">
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <Label htmlFor="address" className="text-gray-700">
                              Address
                            </Label>
                            <Input
                              onChange={inputHandle}
                              value={state.address}
                              type="text"
                              className="w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground"
                              name="address"
                              placeholder="House no / building / street / area"
                              id="address"
                            />
                          </div>
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <Label htmlFor="phone" className="text-gray-700">
                              Phone
                            </Label>
                            <Input
                              onChange={inputHandle}
                              value={state.phone}
                              type="text"
                              className="w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground"
                              name="phone"
                              placeholder="Phone"
                              id="phone"
                            />
                          </div>
                        </div>

                        <div className="flex md:flex-col md:gap-2 w-full gap-5">
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <Label htmlFor="post" className="text-gray-700">
                              Postal code
                            </Label>
                            <Input
                              onChange={inputHandle}
                              value={state.post}
                              type="text"
                              className="w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground"
                              name="post"
                              placeholder="Postal code"
                              id="post"
                            />
                          </div>
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <Label htmlFor="province" className="text-gray-700">
                              Province
                            </Label>
                            <Input
                              onChange={inputHandle}
                              value={state.province}
                              type="text"
                              className="w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground"
                              name="province"
                              placeholder="Province"
                              id="province"
                            />
                          </div>
                        </div>

                        <div className="flex md:flex-col md:gap-2 w-full gap-5">
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <Label htmlFor="city" className="text-gray-700">
                              City
                            </Label>
                            <Input
                              onChange={inputHandle}
                              value={state.city}
                              type="text"
                              className="w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground"
                              name="city"
                              placeholder="City"
                              id="city"
                            />
                          </div>
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <Label htmlFor="area" className="text-gray-700">
                              Area
                            </Label>
                            <Input
                              onChange={inputHandle}
                              value={state.area}
                              type="text"
                              className="w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground"
                              name="area"
                              placeholder="Area"
                              id="area"
                            />
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <Button
                            type="submit"
                            className="px-5 py-2 rounded-full bg-white text-black hover:bg-gray-100"
                          >
                            Continue
                          </Button>
                        </div>
                      </form>
                    </>
                  )}
                  {res && (
                    <div className="flex flex-col gap-2">
                      <h2 className="text-gray-900 font-semibold pb-2">
                        Deliver to {state.name}
                      </h2>
                      <p>
                        <span className="bg-[#FFE11B] text-black text-xs font-bold mr-2 px-4 py-1 rounded">
                          Location
                        </span>
                        <span className="text-gray-700 text-sm">
                          {state.address} {state.province} {state.city}{" "}
                          {state.area}, {state.country}
                        </span>
                        <span
                          onClick={() => setRes(false)}
                          className="text-[#FFE11B] cursor-pointer ml-2"
                        >
                          change
                        </span>
                      </p>
                    </div>
                  )}
                </div>

                {products.map((p, i) => (
                  <div key={i} className="flex bg-white border border-gray-100 shadow-sm p-4 flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <h2 className="text-md text-gray-900 flex items-center">
                        {p.shopName} <Package size={18} className="ml-2" />
                      </h2>
                      {state.country && (
                        <div className="text-gray-700 text-sm">
                          Shipping to:{" "}
                          <span className="font-semibold">{state.country}</span>
                        </div>
                      )}
                    </div>
                    {p.products.map((pt, j) => (
                      <ShippingProductDisplay
                        key={j}
                        product={pt}
                        state={state}
                        invalidProducts={invalidProducts}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="w-[33%] md-lg:w-full sm:mt-3">
              <div className="pl-3 md-lg:pl-0">
                <div className="bg-white border border-gray-100 shadow-sm p-3 text-gray-900 flex flex-col gap-8">
                  <h2 className="text-xl font-bold flex items-center">
                    Order summary <ScrollText className="ml-2" />
                  </h2>
                  <div className="flex justify-between items-center">
                    <span>Items Total</span>
                    <span> {formatCurrency(price, currencyCode, exchangeLoaded)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Delivery Fee</span>
                    <span>{formatCurrency(totalShippingCost, currencyCode, exchangeLoaded)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>OMAH Service Fee</span>
                    <span>{formatCurrency(omahServiceFee, currencyCode, exchangeLoaded)}</span>
                  </div>
                  <div className="flex justify-between items-center font-bold">
                    <span>Total Payment</span>
                    <span>{formatCurrency(totalOrderPrice, currencyCode, exchangeLoaded)}</span>
                  </div>

                  {/* Installment payment selector */}
                  {installmentEligible && (
                    <div className="border border-gray-200 rounded-md p-3 flex flex-col gap-3">
                      <p className="text-sm font-semibold text-gray-800">Payment Method</p>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="full"
                            checked={paymentMethod === 'full'}
                            onChange={() => { setPaymentMethod('full'); setNumInstallments(null); }}
                          />
                          Pay in Full ({formatCurrency(totalOrderPrice, currencyCode, exchangeLoaded)})
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="installment"
                            checked={paymentMethod === 'installment'}
                            onChange={() => { setPaymentMethod('installment'); setNumInstallments(installmentSettings.plans[0] || 2); }}
                          />
                          Pay in Installments
                        </label>
                      </div>

                      {paymentMethod === 'installment' && (
                        <div className="flex flex-col gap-2 mt-1">
                          <p className="text-xs text-gray-500">Choose a plan:</p>
                          <div className="flex gap-2 flex-wrap">
                            {installmentSettings.plans.map(n => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setNumInstallments(n)}
                                className={`px-3 py-1 rounded-full border text-xs font-semibold transition-all ${
                                  numInstallments === n
                                    ? 'bg-[#ffe11b] border-[#ffe11b] text-black'
                                    : 'bg-white border-gray-300 text-gray-700 hover:border-gray-500'
                                }`}
                              >
                                {n}x Monthly
                              </button>
                            ))}
                          </div>
                          {numInstallments && (
                            <div className="bg-gray-50 rounded p-2 text-xs text-gray-600 flex flex-col gap-1 mt-1">
                              <div className="flex justify-between">
                                <span>Per installment</span>
                                <span className="font-semibold">{formatCurrency(firstInstallmentAmount, currencyCode, exchangeLoaded)}</span>
                              </div>
                              {interestRate > 0 && (
                                <div className="flex justify-between">
                                  <span>Interest ({interestRate}%)</span>
                                  <span>{formatCurrency(totalWithInterest - totalOrderPrice, currencyCode, exchangeLoaded)}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-semibold border-t border-gray-200 pt-1 mt-1">
                                <span>Total payable</span>
                                <span>{formatCurrency(totalWithInterest, currencyCode, exchangeLoaded)}</span>
                              </div>
                              <p className="text-gray-400 mt-1">First payment due today. Subsequent payments monthly.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={placeOrder}
                    disabled={!res || invalidProducts.length > 0 || (paymentMethod === 'installment' && !numInstallments)}
                    className={`px-5 py-[6px] rounded-full ${
                      res && invalidProducts.length === 0 && (paymentMethod !== 'installment' || numInstallments)
                        ? "bg-white text-black hover:bg-gray-100"
                        : "cursor-not-allowed opacity-50"
                    } text-sm uppercase`}
                  >
                    Place Order
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Shipping Not Available Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white border border-gray-200 text-gray-900 p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex items-center gap-2 text-red-500 mb-4">
              <AlertTriangle className="h-5 w-5" />
              <h2 className="text-lg font-semibold">Shipping Not Available</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Some items in your cart cannot be shipped to {state.country}.
              Please remove these items from your cart or choose a different
              shipping destination.
            </p>
            <div className="flex justify-end">
              <Link to="/cart">
                <Button className="bg-white text-black hover:bg-gray-100 mr-2">
                  Update Cart
                </Button>
              </Link>
              <Button
                onClick={() => {
                  setShowModal(false);
                  setRes(false);
                }}
                className="bg-white text-black hover:bg-gray-100"
              >
                Update Shipping Details
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shipping;
